import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useLocation } from "wouter";
import { useGetContract, useUpdateContract, useStartRenewal, getGetContractQueryKey, getListContractsQueryKey, getListRenewalsQueryKey } from "@workspace/api-client-react";
import { useOrg } from "@/lib/orgContext";
import { useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { DemoExportGate, useDemoExportLocked } from "@/components/DemoExportGate";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { ArrowLeft, FileText, Calendar, DollarSign, Award, AlertTriangle, RefreshCw, Car, Download, GitBranch, Lock, CheckCircle2 } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ContractDocuments } from "@/components/contract-documents";
import { ContractGuestsPanel } from "@/components/ContractGuestsPanel";
import { ContractHistory } from "@/components/ContractHistory";
import { ApprovalSection } from "@/components/ApprovalSection";
import { CommentsSection } from "@/components/CommentsSection";
import { BuyoutCalculator } from "@/components/buyout-calculator";
import { LockedPreview } from "@/components/LockedPreview";
import { BuyoutTermsEditor } from "@/components/buyout-terms-editor";
import { MaskedValue } from "@/components/MaskedValue";
import { DEFAULT_TERMINATION_RULES } from "@workspace/buyout";
import { BonusRepeater, AllowanceRepeater, type ContractBonus, type ContractAllowance } from "@/components/bonus-repeater";
import { Pencil, X } from "lucide-react";
import { SampleBadge } from "@/components/SampleBadge";
import { ContractClauses } from "@/components/ContractClauses";
import { CompliancePanel } from "@/components/CompliancePanel";
import { getGetContractComplianceQueryKey as getContractComplianceQueryKey } from "@workspace/api-client-react";
import { RouteForApprovalDialog } from "@/components/RouteForApprovalDialog";
import { SendExternalDialog } from "@/components/SendExternalDialog";
import { NegotiationTracker } from "@/components/NegotiationTracker";
import { ContractCompare } from "@/components/ContractCompare";
import type { ContractData } from "@/lib/mergeFields";
import { useAuth } from "@clerk/react";
import { LifecycleStateBadge } from "@/components/LifecycleStateBadge";
import { LifecycleActionsPanel } from "@/components/LifecycleActionsPanel";
import { LifecycleActionsMenu } from "@/components/LifecycleActionsMenu";
import { WorkflowStatusBar } from "@/components/WorkflowStatusBar";
import { PrimaryWorkflowAction } from "@/components/PrimaryWorkflowAction";
import {
  type ApprovalStatusLight as WorkflowApprovalStatus,
  type ApprovalStageId,
} from "@/lib/workflowStages";
import { NegotiationRoundsTimeline } from "@/components/NegotiationRoundsTimeline";
import { ApproverNoteBadge, useUnresolvedApproverNotes } from "@/components/ApproverNoteBadge";
import { handleApiError } from "@/lib/handleApiError";
import { CustomFieldsSection, saveCustomFieldValues } from "@/components/CustomFieldsSection";
import { TerminateOffboardingDialog } from "@/components/TerminateOffboardingDialog";
import { ReviseContractTermsDialog } from "@/components/ReviseContractTermsDialog";

const BONUS_TYPE_LABELS: Record<string, string> = {
  signing: "Signing",
  relocation: "Relocation",
  retention: "Retention",
  performance: "Performance",
  academic: "Academic",
  media_apparel: "Media / Apparel",
  other: "Other",
};

const PAYMENT_TIMING_LABELS: Record<string, string> = {
  one_time_signing: "One-time at signing",
  on_trigger: "On trigger",
  annual: "Annual",
  custom_date: "Custom date",
};

const ALLOWANCE_TIMING_LABELS: Record<string, string> = {
  monthly: "Monthly",
  annual: "Annual",
  one_time: "One-time",
  custom: "Custom",
};

function contractStatusVariant(status: string): "green" | "gold" | "red" | "muted" {
  if (status === "active") return "green";
  if (status === "pending") return "gold";
  if (status === "expired") return "red";
  return "muted";
}

function BonusesTable({ bonuses }: { bonuses: ContractBonus[] }) {
  if (!bonuses || bonuses.length === 0) {
    return <div className="text-sm text-muted-foreground italic">No bonuses specified.</div>;
  }

  const grouped = bonuses.reduce<Record<string, ContractBonus[]>>((acc, b) => {
    const key = BONUS_TYPE_LABELS[b.type] ?? b.type;
    if (!acc[key]) acc[key] = [];
    acc[key].push(b);
    return acc;
  }, {});

  const total = bonuses.reduce((s, b) => s + (b.amount || 0), 0);
  const hasConditional = bonuses.some((b) => b.paymentTiming === "on_trigger");
  const hasRecurring = bonuses.some((b) => b.isRecurring);

  return (
    <div className="space-y-4">
      {Object.entries(grouped).map(([group, items]) => (
        <div key={group}>
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">{group}</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-1.5 pr-4 font-medium text-muted-foreground">Label</th>
                  <th className="text-right py-1.5 pr-4 font-medium text-muted-foreground">Amount</th>
                  <th className="text-left py-1.5 pr-4 font-medium text-muted-foreground">Payment</th>
                  <th className="text-left py-1.5 font-medium text-muted-foreground">Condition</th>
                </tr>
              </thead>
              <tbody>
                {items.map((b) => (
                  <tr key={b.id} className="border-b border-border/50 hover:bg-muted/30">
                    <td className="py-2 pr-4">
                      <div className="font-medium">{b.label || b.customLabel || "—"}</div>
                      {b.isRecurring && (
                        <div className="text-xs text-muted-foreground">Recurring{b.recurringFrequency ? ` — ${b.recurringFrequency}` : ""}</div>
                      )}
                    </td>
                    <td className="py-2 pr-4 text-right font-medium kpi-value">
                      {b.amount ? `$${b.amount.toLocaleString()}` : <span className="text-muted-foreground">TBD</span>}
                    </td>
                    <td className="py-2 pr-4 text-muted-foreground">
                      {PAYMENT_TIMING_LABELS[b.paymentTiming] ?? b.paymentTiming}
                      {b.paymentDate && <div className="text-xs">{b.paymentDate}</div>}
                    </td>
                    <td className="py-2 text-muted-foreground max-w-[250px]">
                      <div className="truncate" title={b.condition}>{b.condition || "—"}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      <div className="rounded-lg bg-muted/60 border border-border px-4 py-3 text-sm">
        <div className="flex justify-between font-semibold">
          <span>Total Bonuses</span>
          <span className="kpi-value">${total.toLocaleString()}</span>
        </div>
        {(hasConditional || hasRecurring) && (
          <p className="text-xs text-muted-foreground mt-1">* Includes conditional and/or recurring amounts — actual payouts depend on triggers being met.</p>
        )}
      </div>
    </div>
  );
}

function AllowancesTable({ allowances }: { allowances: ContractAllowance[] }) {
  if (!allowances || allowances.length === 0) {
    return <div className="text-sm text-muted-foreground italic">No allowances specified.</div>;
  }

  const total = allowances.reduce((s, a) => s + (a.amount || 0), 0);

  return (
    <div className="space-y-4">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-1.5 pr-4 font-medium text-muted-foreground">Category</th>
              <th className="text-left py-1.5 pr-4 font-medium text-muted-foreground">Label</th>
              <th className="text-right py-1.5 pr-4 font-medium text-muted-foreground">Amount</th>
              <th className="text-left py-1.5 font-medium text-muted-foreground">Frequency</th>
            </tr>
          </thead>
          <tbody>
            {allowances.map((a) => (
              <tr key={a.id} className="border-b border-border/50 hover:bg-muted/30">
                <td className="py-2 pr-4 font-medium">{a.type}</td>
                <td className="py-2 pr-4">
                  {a.label || "—"}
                  {a.notes && <div className="text-xs text-muted-foreground">{a.notes}</div>}
                </td>
                <td className="py-2 pr-4 text-right font-medium kpi-value">
                  {a.amount ? `$${a.amount.toLocaleString()}` : <span className="text-muted-foreground">—</span>}
                </td>
                <td className="py-2 text-muted-foreground">
                  {ALLOWANCE_TIMING_LABELS[a.paymentTiming] ?? a.paymentTiming}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="rounded-lg bg-muted/60 border border-border px-4 py-3 text-sm">
        <div className="flex justify-between font-semibold">
          <span>Total Allowances</span>
          <span className="kpi-value">${total.toLocaleString()}</span>
        </div>
      </div>
    </div>
  );
}

async function triggerDownload(url: string, filename: string) {
  const res = await fetch(url);
  if (!res.ok) throw new Error("Download failed");
  const blob = await res.blob();
  const objUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objUrl);
}

interface ApprovalStatusLight {
  stage: string;
  status: string;
  isLocked: boolean;
  isExecuted: boolean;
  hasApproval: boolean;
}

export default function ContractDetail() {
  const { id } = useParams<{ id: string }>();
  const contractId = parseInt(id, 10);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { getToken } = useAuth();
  const [, setLocation] = useLocation();
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [isExportingSummary, setIsExportingSummary] = useState(false);
  const [routeDialogOpen, setRouteDialogOpen] = useState(false);
  const [sendExternalOpen, setSendExternalOpen] = useState(false);
  const [terminateDialogOpen, setTerminateDialogOpen] = useState(false);
  const [reviseTermsOpen, setReviseTermsOpen] = useState(false);
  const [approvalStatus, setApprovalStatus] = useState<ApprovalStatusLight | null>(null);
  const [cfValues, setCfValues] = useState<Record<string, unknown>>({});
  const [cfSaving, setCfSaving] = useState(false);
  
  const { data: contract, isLoading } = useGetContract(contractId, { 
    query: { enabled: !!contractId, queryKey: getGetContractQueryKey(contractId) } 
  });

  // Redirect term sheets to the dedicated /term-sheets/:id view.
  useEffect(() => {
    if (contract && (contract as { documentKind?: string }).documentKind === "term_sheet") {
      setLocation(`/term-sheets/${contractId}`, { replace: true });
    }
  }, [contract, contractId, setLocation]);
  
  const updateContract = useUpdateContract();
  const startRenewal = useStartRenewal();
  const { org } = useOrg();
  const isAdmin = org?.role === "admin";
  const demoIpLocked = useDemoExportLocked();
  const personas = org?.personas ?? [];

  const refreshApprovalStatus = useCallback(async () => {
    if (!contractId) return;
    try {
      const token = await getToken();
      const res = await fetch(`/api/contracts/${contractId}/approval`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        const approval = data.approval;
        if (approval) {
          setApprovalStatus({
            stage: approval.currentStage,
            status: approval.status,
            isLocked: approval.currentStage === "signature" && approval.status === "in_progress",
            isExecuted: approval.status === "executed",
            hasApproval: true,
          });
        } else {
          setApprovalStatus({ stage: "draft", status: "none", isLocked: false, isExecuted: false, hasApproval: false });
        }
      }
    } catch { /* ignore */ }
  }, [contractId, getToken]);

  useEffect(() => { void refreshApprovalStatus(); }, [refreshApprovalStatus]);

  const { byField: unresolvedNotesByField, notes: unresolvedNotes, approvalId: unresolvedNotesApprovalId, refresh: refreshUnresolvedNotes } = useUnresolvedApproverNotes(contractId);
  const unresolvedFieldKeys = Object.keys(unresolvedNotesByField);

  const [lifecycleRefreshTrigger, setLifecycleRefreshTrigger] = useState(0);
  const [negotiationRoundsRefreshTrigger, setNegotiationRoundsRefreshTrigger] = useState(0);

  const handleLifecycleChanged = () => {
    setLifecycleRefreshTrigger((n) => n + 1);
    setNegotiationRoundsRefreshTrigger((n) => n + 1);
    queryClient.invalidateQueries({ queryKey: getGetContractQueryKey(contractId) });
    queryClient.invalidateQueries({ queryKey: getListContractsQueryKey() });
  };

  const validTabs = ["details", "clauses", "negotiation", "approval", "buyout", "documents", "comments", "history", "compare"];
  const getTabFromHash = () => {
    const hash = window.location.hash.replace(/^#/, "");
    return validTabs.includes(hash) ? hash : "details";
  };
  const [activeTab, setActiveTab] = useState<string>(getTabFromHash);
  useEffect(() => {
    const onHashChange = () => setActiveTab(getTabFromHash());
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  const [editingBonuses, setEditingBonuses] = useState(false);
  const [editingAllowances, setEditingAllowances] = useState(false);
  const [draftBonuses, setDraftBonuses] = useState<ContractBonus[]>([]);
  const [draftAllowances, setDraftAllowances] = useState<ContractAllowance[]>([]);
  const [hasComplianceBlockers, setHasComplianceBlockers] = useState(false);
  const [failingRuleTypes, setFailingRuleTypes] = useState<Set<string>>(new Set());
  const [complianceRefreshTrigger, setComplianceRefreshTrigger] = useState(0);

  const handleSaveBonuses = () => {
    updateContract.mutate(
      { id: contractId, data: { bonuses: draftBonuses } },
      {
        onSuccess: () => {
          toast({ title: "Bonuses saved", description: "Contract bonuses have been updated." });
          queryClient.invalidateQueries({ queryKey: getGetContractQueryKey(contractId) });
          queryClient.invalidateQueries({ queryKey: getListContractsQueryKey() });
          queryClient.invalidateQueries({ queryKey: getContractComplianceQueryKey(contractId) });
          void refreshUnresolvedNotes();
          setEditingBonuses(false);
        },
        onError: (err) => handleApiError(toast, err, { fallbackTitle: "Could not save bonuses" }),
      }
    );
  };

  const handleSaveAllowances = () => {
    updateContract.mutate(
      { id: contractId, data: { allowances: draftAllowances } },
      {
        onSuccess: () => {
          toast({ title: "Allowances saved", description: "Contract allowances have been updated." });
          queryClient.invalidateQueries({ queryKey: getGetContractQueryKey(contractId) });
          queryClient.invalidateQueries({ queryKey: getListContractsQueryKey() });
          queryClient.invalidateQueries({ queryKey: getContractComplianceQueryKey(contractId) });
          void refreshUnresolvedNotes();
          setEditingAllowances(false);
        },
        onError: (err) => handleApiError(toast, err, { fallbackTitle: "Could not save allowances" }),
      }
    );
  };

  const handleStartRenewal = () => {
    startRenewal.mutate(
      { data: { contractId, stage: "negotiation" } },
      {
        onSuccess: () => {
          toast({ title: "Renewal Started", description: "Tracked in the renewals pipeline." });
          queryClient.invalidateQueries({ queryKey: getListRenewalsQueryKey() });
        },
        onError: (err) => handleApiError(toast, err, { fallbackTitle: "Could not start renewal" }),
      }
    );
  };

  const handleStatusChange = (newStatus: string) => {
    updateContract.mutate({ id: contractId, data: { status: newStatus as any } }, {
      onSuccess: () => {
        toast({ title: "Status Updated", description: `Contract status changed to ${newStatus}.` });
        queryClient.invalidateQueries({ queryKey: getGetContractQueryKey(contractId) });
        queryClient.invalidateQueries({ queryKey: getListContractsQueryKey() });
      }
    });
  };

  const handleExportPdf = async () => {
    setIsExportingPdf(true);
    try {
      const coachLastName =
        (contract as { coachName?: string | null })?.coachName
          ?.split(/\s+/)
          .filter(Boolean)
          .pop()
          ?.toLowerCase() ?? "contract";
      const date = new Date().toISOString().split("T")[0];
      await triggerDownload(
        `/api/contracts/${contractId}/export.pdf`,
        `${coachLastName}-${date}-contract-${contractId}.pdf`
      );
    } catch {
      toast({ title: "Export failed", description: "Could not generate the PDF. Please try again.", variant: "destructive" });
    } finally {
      setIsExportingPdf(false);
    }
  };

  const handleExportSummary = async () => {
    setIsExportingSummary(true);
    try {
      const coachLastName =
        (contract as { coachName?: string | null })?.coachName
          ?.split(/\s+/)
          .filter(Boolean)
          .pop()
          ?.toLowerCase() ?? "contract";
      const date = new Date().toISOString().split("T")[0];
      await triggerDownload(
        `/api/contracts/${contractId}/summary.pdf`,
        `${coachLastName}-${date}-contract-summary-${contractId}.pdf`
      );
    } catch {
      toast({ title: "Export failed", description: "Could not generate the summary PDF. Please try again.", variant: "destructive" });
    } finally {
      setIsExportingSummary(false);
    }
  };

  if (isLoading || !contract) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-[300px]" />
        <Skeleton className="h-[400px] w-full" />
      </div>
    );
  }

  const financialsMasked = !!(contract as { financialsMasked?: boolean }).financialsMasked;
  const bonuses = (contract.bonuses ?? []) as ContractBonus[];
  const allowances = (contract.allowances ?? []) as ContractAllowance[];
  const totalBonuses = bonuses.reduce((s, b) => s + (b.amount || 0), 0);
  const totalAllowances = allowances.reduce((s, a) => s + (a.amount || 0), 0);

  const lifecycleState = (contract as { lifecycleState?: string }).lifecycleState ?? "draft";
  const canReviseTerms =
    isAdmin &&
    !financialsMasked &&
    !approvalStatus?.isExecuted &&
    !approvalStatus?.isLocked &&
    lifecycleState !== "locked" &&
    lifecycleState !== "signed" &&
    contract.status !== "terminated";

  const isInProgress = approvalStatus?.status === "in_progress" && !approvalStatus?.isLocked;

  // Adapt local approval shape to WorkflowApprovalStatus for stage mapping.
  const workflowApproval: WorkflowApprovalStatus | null = approvalStatus
    ? {
        hasApproval: approvalStatus.hasApproval,
        status: approvalStatus.hasApproval
          ? (approvalStatus.status as WorkflowApprovalStatus["status"])
          : "none",
        currentStage: (approvalStatus.stage as ApprovalStageId | "draft") ?? null,
      }
    : null;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" asChild>
            <Link href="/contracts"><ArrowLeft className="h-4 w-4" /></Link>
          </Button>
          <div className="page-header">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 data-testid="text-contract-detail-title">
                {contract.coachName ?? "Untitled coach"}
                <span className="text-muted-foreground font-normal"> — Contract</span>
              </h1>
              <span
                className="text-xs font-mono text-muted-foreground bg-muted/60 border rounded px-1.5 py-0.5"
                title={`Contract ID #${contract.id}`}
                data-testid="text-contract-id-chip"
              >
                #{contract.id}
              </span>
              <Badge variant={contractStatusVariant(contract.status)}>{contract.status}</Badge>
              <LifecycleStateBadge state={(contract as any).lifecycleState ?? "draft"} size="sm" />
              {contract.isSample && <SampleBadge />}
              {approvalStatus?.isLocked && (
                <Badge variant="outline" className="border-amber-400 text-amber-700 bg-amber-50 flex items-center gap-1">
                  <Lock className="h-3 w-3" /> Locked — Pending Signature
                </Badge>
              )}
              {approvalStatus?.isExecuted && (
                <Badge className="bg-green-600 hover:bg-green-600 flex items-center gap-1">
                  <CheckCircle2 className="h-3 w-3" /> Executed
                </Badge>
              )}
              {isInProgress && (
                <Badge variant="secondary" className="flex items-center gap-1">
                  <GitBranch className="h-3 w-3" /> In Review
                </Badge>
              )}
            </div>
            <p className="flex items-center gap-1.5 flex-wrap">
              <Link href={`/coaches/${contract.coachId}`} className="hover:underline text-primary">{contract.coachName}</Link> · {contract.sport} ({contract.contractType.replace('_', ' ')})
              <ApproverNoteBadge notes={unresolvedNotesByField["contractType"]} field="contractType" approvalId={unresolvedNotesApprovalId} onResolved={() => void refreshUnresolvedNotes()} />
            </p>
          </div>
        </div>
        
        <div className="flex gap-2 flex-wrap">
          {/* Context-aware primary CTA. */}
          <PrimaryWorkflowAction
            contractId={contractId}
            lifecycleState={(contract as any).lifecycleState ?? "draft"}
            approval={workflowApproval}
            isAdmin={isAdmin}
            onRouteForApproval={() => setRouteDialogOpen(true)}
            onSendExternal={() => setSendExternalOpen(true)}
          />
          <DemoExportGate label="Export PDF" variant="outline" size="default">
            <Button variant="outline" onClick={handleExportPdf} disabled={isExportingPdf}>
              <Download className="h-4 w-4 mr-2" />
              {isExportingPdf ? "Generating..." : "Export PDF"}
            </Button>
          </DemoExportGate>
          <DemoExportGate label="Plain-English Summary" variant="outline" size="default">
            <Button variant="outline" onClick={handleExportSummary} disabled={isExportingSummary}>
              <FileText className="h-4 w-4 mr-2" />
              {isExportingSummary ? "Generating..." : "Plain-English Summary"}
            </Button>
          </DemoExportGate>
          {isAdmin && (
            <>
              <LifecycleActionsMenu
                contractId={contractId}
                lifecycleState={(contract as any).lifecycleState ?? "draft"}
                isAdmin={isAdmin}
                personas={personas}
                onStateChanged={handleLifecycleChanged}
              />
              {(contract.status === 'active' || contract.status === 'pending') && (
                <Button variant="outline" onClick={handleStartRenewal} disabled={startRenewal.isPending}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  {startRenewal.isPending ? "Starting..." : "Start Renewal"}
                </Button>
              )}
              {contract.status === 'pending' && (
                <Button onClick={() => handleStatusChange('active')} className="bg-green-600 hover:bg-green-700">Activate Contract</Button>
              )}
              {contract.status === 'active' && (
                <Button
                  variant="outline"
                  onClick={() => setTerminateDialogOpen(true)}
                  className="text-destructive hover:bg-destructive/10"
                  data-testid="button-terminate-contract"
                >
                  Terminate
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {contract.status === "active" && (
        <TerminateOffboardingDialog
          open={terminateDialogOpen}
          onOpenChange={setTerminateDialogOpen}
          contract={{
            id: contract.id,
            coachId: contract.coachId,
            coachName: contract.coachName,
            startDate: contract.startDate,
            endDate: contract.endDate,
            annualSalary: contract.annualSalary,
            buyoutClause: contract.buyoutClause,
            buyoutSchedule: contract.buyoutSchedule,
            terminationRules: contract.terminationRules,
            mitigationRequired: contract.mitigationRequired,
            terminationConditions: contract.terminationConditions,
            notes: contract.notes,
            financialsMasked: !!(contract as { financialsMasked?: boolean }).financialsMasked,
          }}
        />
      )}

      {/* Workflow stage bar. */}
      <WorkflowStatusBar
        contractId={contractId}
        lifecycleState={(contract as any).lifecycleState ?? "draft"}
        approval={workflowApproval}
        blocked={
          (workflowApproval?.status === "rejected" || workflowApproval?.status === "returned") ||
          (((contract as any).lifecycleState === "out_for_negotiation" ||
            (contract as any).lifecycleState === "counter_received") &&
            ((contract as any).openIssuesCount ?? 0) > 0)
        }
      />


      {/* Unresolved approver notes banner. */}
      {unresolvedNotes.length > 0 && (
        <div
          className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 space-y-2"
          data-testid="unresolved-approver-notes-banner"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-600" />
            <div className="flex-1">
              <div className="font-medium">
                {unresolvedNotes.length} unresolved approver note{unresolvedNotes.length === 1 ? "" : "s"} on this contract
              </div>
              <div className="text-xs text-amber-800/80 mt-0.5">
                Look for the amber pin next to each flagged field below, or open one from the list here.
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 pl-7" data-testid="unresolved-approver-notes-list">
            {unresolvedFieldKeys.map((field) => {
              const items = unresolvedNotesByField[field] ?? [];
              const label = items.find((i) => i.fieldLabel)?.fieldLabel ?? field;
              return (
                <div
                  key={field}
                  className="inline-flex items-center gap-1.5 rounded border border-amber-200 bg-white px-2 py-1 text-xs"
                  data-testid={`unresolved-field-row-${field}`}
                >
                  <span className="font-medium text-amber-900">{label}</span>
                  <ApproverNoteBadge notes={items} field={`banner-${field}`} approvalId={unresolvedNotesApprovalId} onResolved={() => void refreshUnresolvedNotes()} />
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Route for Approval Dialog */}
      {isAdmin && (
        <RouteForApprovalDialog
          open={routeDialogOpen}
          onOpenChange={setRouteDialogOpen}
          contractId={contractId}
          hasComplianceBlockers={hasComplianceBlockers}
          isAmendment={approvalStatus?.status !== "none" && approvalStatus?.hasApproval === true}
          onSubmitted={() => {
            void refreshApprovalStatus();
            void refreshUnresolvedNotes();
            setActiveTab("approval");
          }}
        />
      )}

      {/* Send Externally Dialog. */}
      {isAdmin && (
        <SendExternalDialog
          open={sendExternalOpen}
          onOpenChange={setSendExternalOpen}
          contractId={contractId}
          onSuccess={() => {
            setSendExternalOpen(false);
            queryClient.invalidateQueries({ queryKey: getGetContractQueryKey(contractId) });
            queryClient.invalidateQueries({ queryKey: getListContractsQueryKey() });
            setActiveTab("negotiation");
          }}
        />
      )}

      <ReviseContractTermsDialog
        open={reviseTermsOpen}
        onOpenChange={setReviseTermsOpen}
        contractId={contractId}
        current={{
          annualSalary: contract.annualSalary ?? null,
          startDate: contract.startDate ?? null,
          endDate: contract.endDate ?? null,
          buyoutClause: contract.buyoutClause ?? null,
        }}
        onSaved={() => {
          setComplianceRefreshTrigger((n) => n + 1);
          void refreshUnresolvedNotes();
          setNegotiationRoundsRefreshTrigger((n) => n + 1);
        }}
      />

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        {/* Tab order follows the contract workflow: Details → Clauses → Negotiation → Approval → Documents → Comments → History → Compare. */}
        <TabsList className="w-full overflow-x-auto justify-start md:justify-center md:w-auto">
          <TabsTrigger value="details">Terms & Compensation</TabsTrigger>
          <TabsTrigger value="clauses">Clauses & Bonuses</TabsTrigger>
          <TabsTrigger value="buyout">Buyout Calculator</TabsTrigger>
          <TabsTrigger value="custom_fields">Custom Fields</TabsTrigger>
          <TabsTrigger value="negotiation" className="relative">
            Negotiation
            {((contract as any).openIssuesCount ?? 0) > 0 ? (
              <span
                className="ml-1.5 inline-flex items-center justify-center text-[10px] leading-none px-1.5 py-0.5 rounded-md bg-amber-100 text-amber-800 border border-amber-200 font-semibold"
                data-testid="badge-tab-open-issues"
              >
                {(contract as any).openIssuesCount} open
              </span>
            ) : (
              ["out_for_negotiation", "counter_received"].includes((contract as any).lifecycleState ?? "draft") && (
                <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-orange-500" />
              )
            )}
          </TabsTrigger>
          <TabsTrigger value="approval" className="relative">
            Approval
            {isInProgress && (
              <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-amber-500" />
            )}
          </TabsTrigger>
          <TabsTrigger value="documents">Documents</TabsTrigger>
          <TabsTrigger value="comments">Comments</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">History</TabsTrigger>
          <TabsTrigger value="compare">Compare</TabsTrigger>
        </TabsList>

        <TabsContent value="approval" className="mt-6 space-y-4">
          <CompliancePanel contractId={contractId} onHasBlockers={setHasComplianceBlockers} onFailingRuleTypes={setFailingRuleTypes} refreshTrigger={complianceRefreshTrigger} />
          <ApprovalSection contractId={contract.id} />
        </TabsContent>

        <TabsContent value="negotiation" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-1">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm">Lifecycle Actions</CardTitle>
                </CardHeader>
                <CardContent>
                  <LifecycleActionsPanel
                    contractId={contractId}
                    lifecycleState={(contract as any).lifecycleState ?? "draft"}
                    isAdmin={isAdmin}
                    personas={personas}
                    onStateChanged={handleLifecycleChanged}
                  />
                </CardContent>
              </Card>
            </div>
            <div className="lg:col-span-2 space-y-6">
              <NegotiationRoundsTimeline
                contractId={contractId}
                refreshTrigger={negotiationRoundsRefreshTrigger}
                isAdmin={isAdmin}
                deliveryAlertsMutedAt={(contract as any).deliveryAlertsMutedAt ?? null}
                onDeliveryAlertsMutedChange={() => {
                  queryClient.invalidateQueries({ queryKey: getGetContractQueryKey(contractId) });
                }}
                // Task #562 — Hand the contract lifecycle + holder to the timeline so it can render
                // the at-a-glance pipeline status pill without a second roundtrip.
                lifecycleState={(contract as any).lifecycleState ?? null}
                negotiationHolder={(contract as any).negotiationHolder ?? null}
              />
              <NegotiationTracker contractId={contract.id} isAdmin={isAdmin} />
            </div>
          </div>
        </TabsContent>
        
        <TabsContent value="details" className="mt-6 space-y-6">
          {canReviseTerms && (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-lg border border-border bg-muted/40 px-4 py-3">
              <p className="text-sm text-muted-foreground">
                Reflect negotiated salary, dates, or buyout here. Bonuses and allowances edit on the
                Clauses tab. After saving, send another revision from Negotiation.
              </p>
              <Button
                variant="outline"
                size="sm"
                className="shrink-0"
                onClick={() => setReviseTermsOpen(true)}
                data-testid="button-revise-contract-terms"
              >
                <Pencil className="h-3.5 w-3.5 mr-1.5" /> Revise Terms
              </Button>
            </div>
          )}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="flex flex-row items-start justify-between pb-2 space-y-0 p-4">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wide leading-tight">Annual Salary</CardTitle>
                  {failingRuleTypes.has("salary_threshold") && (
                    <span title="Salary policy rule violation" className="inline-block w-2 h-2 rounded-full bg-red-500 shrink-0" />
                  )}
                  <ApproverNoteBadge notes={unresolvedNotesByField["annualSalary"]} field="annualSalary" approvalId={unresolvedNotesApprovalId} onResolved={() => void refreshUnresolvedNotes()} />
                </div>
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-field-green-subtle">
                  <DollarSign className="h-4 w-4 text-field-green" />
                </div>
              </CardHeader>
              <CardContent className="p-4 pt-0">
                <div className="text-2xl font-bold kpi-value tracking-tight">
                  {financialsMasked
                    ? <MaskedValue />
                    : typeof contract.annualSalary === "number"
                    ? `$${contract.annualSalary.toLocaleString()}`
                    : <span className="text-muted-foreground text-base font-medium">—</span>}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-start justify-between pb-2 space-y-0 p-4">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wide leading-tight pr-2">Total Bonuses</CardTitle>
                  <ApproverNoteBadge notes={unresolvedNotesByField["bonuses"] ?? unresolvedNotesByField["signingBonus"]} field="bonuses" approvalId={unresolvedNotesApprovalId} onResolved={() => void refreshUnresolvedNotes()} />
                </div>
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-gold-subtle">
                  <Award className="h-4 w-4 text-gold" />
                </div>
              </CardHeader>
              <CardContent className="p-4 pt-0">
                <div className="text-2xl font-bold kpi-value tracking-tight">
                  {financialsMasked
                    ? <MaskedValue />
                    : `$${totalBonuses.toLocaleString()}`}
                </div>
                <p className="text-xs text-muted-foreground mt-1 leading-tight">{bonuses.length} item{bonuses.length !== 1 ? "s" : ""}</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-start justify-between pb-2 space-y-0 p-4">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wide leading-tight pr-2">Total Allowances</CardTitle>
                  <ApproverNoteBadge notes={unresolvedNotesByField["allowances"]} field="allowances" approvalId={unresolvedNotesApprovalId} onResolved={() => void refreshUnresolvedNotes()} />
                </div>
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-muted">
                  <Car className="h-4 w-4 text-muted-foreground" />
                </div>
              </CardHeader>
              <CardContent className="p-4 pt-0">
                <div className="text-2xl font-bold kpi-value tracking-tight">
                  {financialsMasked
                    ? <MaskedValue />
                    : `$${totalAllowances.toLocaleString()}`}
                </div>
                <p className="text-xs text-muted-foreground mt-1 leading-tight">{allowances.length} item{allowances.length !== 1 ? "s" : ""}</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-start justify-between pb-2 space-y-0 p-4">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <CardTitle className="text-xs font-medium text-muted-foreground uppercase tracking-wide leading-tight pr-2">Buyout Clause</CardTitle>
                  <ApproverNoteBadge notes={unresolvedNotesByField["buyoutClause"]} field="buyoutClause" approvalId={unresolvedNotesApprovalId} onResolved={() => void refreshUnresolvedNotes()} />
                </div>
                <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-signal-red-subtle">
                  <AlertTriangle className="h-4 w-4 text-signal-red" />
                </div>
              </CardHeader>
              <CardContent className="p-4 pt-0">
                <div className="text-2xl font-bold kpi-value tracking-tight">
                  {financialsMasked
                    ? <MaskedValue />
                    : contract.buyoutClause
                    ? `$${Number(contract.buyoutClause).toLocaleString()}`
                    : <span className="text-muted-foreground text-base font-medium">—</span>}
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-lg flex items-center gap-2">
                <Calendar className="h-5 w-5 text-muted-foreground"/> Term Details
                {failingRuleTypes.has("term_cap") && (
                  <span title="Contract term policy rule violation" className="inline-block w-2.5 h-2.5 rounded-full bg-red-500" />
                )}
              </CardTitle>
              {canReviseTerms && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setReviseTermsOpen(true)}
                  data-testid="button-revise-term-details"
                >
                  <Pencil className="h-3.5 w-3.5 mr-1.5" /> Revise Terms
                </Button>
              )}
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <div className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
                    Start Date
                    <ApproverNoteBadge notes={unresolvedNotesByField["startDate"]} field="startDate" approvalId={unresolvedNotesApprovalId} onResolved={() => void refreshUnresolvedNotes()} />
                  </div>
                  <div className="mt-1 font-medium kpi-value">{contract.startDate ? format(new Date(contract.startDate), 'MMMM d, yyyy') : '—'}</div>
                </div>
                <div>
                  <div className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
                    End Date
                    <ApproverNoteBadge notes={unresolvedNotesByField["endDate"]} field="endDate" approvalId={unresolvedNotesApprovalId} onResolved={() => void refreshUnresolvedNotes()} />
                  </div>
                  <div className="mt-1 font-medium kpi-value">{contract.endDate ? format(new Date(contract.endDate), 'MMMM d, yyyy') : '—'}</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="clauses" className="mt-6 space-y-6">
          <CompliancePanel contractId={contractId} onHasBlockers={setHasComplianceBlockers} onFailingRuleTypes={setFailingRuleTypes} refreshTrigger={complianceRefreshTrigger} />
          <ContractClauses
            onClausesChanged={() => setComplianceRefreshTrigger((n) => n + 1)}
            contractId={contractId}
            contractData={{
              id: contract.id,
              coachName: contract.coachName ?? "",
              coachFirstName: (contract.coachName ?? "").split(" ")[0] ?? "",
              coachLastName: (contract.coachName ?? "").split(" ").slice(1).join(" ") ?? "",
              coachSport: contract.sport ?? "",
              annualSalary: Number(contract.annualSalary ?? 0),
              signingBonus: bonuses.find((b) => b.type === "signing")?.amount ?? 0,
              totalBonuses,
              totalAllowances,
              startDate: contract.startDate ?? "",
              endDate: contract.endDate ?? "",
              contractType: contract.contractType ?? "",
              buyoutClause: Number(contract.buyoutClause ?? 0),
              buyoutSchedule: (contract.buyoutSchedule ?? []).map((e) => ({ year: e.year, amount: e.amount })),
              status: contract.status ?? "",
              orgName: org?.name ?? "",
              orgInstitutionName: org?.institutionName ?? org?.name ?? "",
              orgDivision: org?.division ?? "",
              orgConference: org?.conference ?? "",
            } satisfies ContractData}
            isAdmin={isAdmin}
            personas={org?.personas ?? []}
          />

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-lg flex items-center gap-2">
                <Award className="h-5 w-5 text-muted-foreground"/> Bonuses
                <ApproverNoteBadge notes={unresolvedNotesByField["bonuses"] ?? unresolvedNotesByField["signingBonus"]} field="bonuses-card" approvalId={unresolvedNotesApprovalId} onResolved={() => void refreshUnresolvedNotes()} />
              </CardTitle>
              {isAdmin && !editingBonuses && (
                <Button variant="outline" size="sm" onClick={() => { setDraftBonuses(bonuses); setEditingBonuses(true); }} data-testid="button-edit-bonuses">
                  <Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit Bonuses
                </Button>
              )}
              {isAdmin && editingBonuses && (
                <Button variant="ghost" size="sm" onClick={() => setEditingBonuses(false)} disabled={updateContract.isPending}>
                  <X className="h-3.5 w-3.5 mr-1.5" /> Cancel
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {isAdmin && editingBonuses ? (
                <div className="space-y-4">
                  <BonusRepeater
                    bonuses={draftBonuses}
                    onChange={setDraftBonuses}
                    notesByField={unresolvedNotesByField}
                    approvalId={unresolvedNotesApprovalId}
                    onNoteResolved={() => void refreshUnresolvedNotes()}
                  />
                  <div className="flex justify-end">
                    <Button onClick={handleSaveBonuses} disabled={updateContract.isPending} data-testid="button-save-bonuses">
                      {updateContract.isPending ? "Saving..." : "Save bonuses"}
                    </Button>
                  </div>
                </div>
              ) : financialsMasked ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground italic py-2">
                  <MaskedValue className="font-mono tracking-widest" />
                  <span>Financial details are restricted for your role.</span>
                </div>
              ) : (
                <BonusesTable bonuses={bonuses} />
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <CardTitle className="text-lg flex items-center gap-2">
                <Car className="h-5 w-5 text-muted-foreground"/> Allowances & Add-ons
                <ApproverNoteBadge notes={unresolvedNotesByField["allowances"]} field="allowances-card" approvalId={unresolvedNotesApprovalId} onResolved={() => void refreshUnresolvedNotes()} />
              </CardTitle>
              {isAdmin && !editingAllowances && (
                <Button variant="outline" size="sm" onClick={() => { setDraftAllowances(allowances); setEditingAllowances(true); }} data-testid="button-edit-allowances">
                  <Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit Allowances
                </Button>
              )}
              {isAdmin && editingAllowances && (
                <Button variant="ghost" size="sm" onClick={() => setEditingAllowances(false)} disabled={updateContract.isPending}>
                  <X className="h-3.5 w-3.5 mr-1.5" /> Cancel
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {isAdmin && editingAllowances ? (
                <div className="space-y-4">
                  <AllowanceRepeater
                    allowances={draftAllowances}
                    onChange={setDraftAllowances}
                    notesByField={unresolvedNotesByField}
                    approvalId={unresolvedNotesApprovalId}
                    onNoteResolved={() => void refreshUnresolvedNotes()}
                  />
                  <div className="flex justify-end">
                    <Button onClick={handleSaveAllowances} disabled={updateContract.isPending} data-testid="button-save-allowances">
                      {updateContract.isPending ? "Saving..." : "Save allowances"}
                    </Button>
                  </div>
                </div>
              ) : financialsMasked ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground italic py-2">
                  <MaskedValue className="font-mono tracking-widest" />
                  <span>Financial details are restricted for your role.</span>
                </div>
              ) : (
                <AllowancesTable allowances={allowances} />
              )}
            </CardContent>
          </Card>

          {(contract.performanceBonuses || unresolvedNotesByField["performanceBonuses"]) && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileText className="h-5 w-5 text-muted-foreground"/> Legacy Performance Bonus Notes
                  <ApproverNoteBadge notes={unresolvedNotesByField["performanceBonuses"]} field="performanceBonuses" approvalId={unresolvedNotesApprovalId} onResolved={() => void refreshUnresolvedNotes()} />
                </CardTitle>
              </CardHeader>
              <CardContent>
                {contract.performanceBonuses ? (
                  <div className="whitespace-pre-wrap text-sm bg-muted/40 p-4 rounded-md">{contract.performanceBonuses}</div>
                ) : (
                  <div className="text-sm text-muted-foreground italic">No legacy performance bonus notes recorded.</div>
                )}
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-muted-foreground"/> Buyout & Termination</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <h4 className="text-sm font-medium mb-2 flex items-center gap-1.5">
                  Buyout Clause
                  <ApproverNoteBadge notes={unresolvedNotesByField["buyoutClause"]} field="buyoutClause-block" approvalId={unresolvedNotesApprovalId} onResolved={() => void refreshUnresolvedNotes()} />
                </h4>
                {financialsMasked ? (
                  <div className="text-sm bg-muted/50 p-4 rounded-md flex items-center gap-2">
                    <MaskedValue className="font-medium" />
                    <span className="text-xs text-muted-foreground">(restricted for viewer role)</span>
                  </div>
                ) : contract.buyoutClause ? (
                  <div className="whitespace-pre-wrap text-sm bg-muted/50 p-4 rounded-md kpi-value font-medium">${Number(contract.buyoutClause).toLocaleString()}</div>
                ) : (
                  <div className="text-sm text-muted-foreground italic">No buyout clause specified.</div>
                )}
              </div>
              <div>
                <h4 className="text-sm font-medium mb-2 flex items-center gap-1.5">
                  Termination Conditions
                  <ApproverNoteBadge notes={unresolvedNotesByField["terminationConditions"]} field="terminationConditions" approvalId={unresolvedNotesApprovalId} onResolved={() => void refreshUnresolvedNotes()} />
                </h4>
                 {contract.terminationConditions ? (
                  <div className="whitespace-pre-wrap text-sm bg-muted/50 p-4 rounded-md">{contract.terminationConditions}</div>
                ) : (
                  <div className="text-sm text-muted-foreground italic">Standard termination conditions apply.</div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="buyout" className="mt-6 space-y-6">
          {financialsMasked ? (
            <div className="flex flex-col items-center justify-center py-16 space-y-3 text-center">
              <MaskedValue className="text-4xl font-mono tracking-[0.4em]" />
              <p className="text-sm text-muted-foreground max-w-xs">
                Buyout and termination figures are restricted for your role. Contact your administrator for access.
              </p>
            </div>
          ) : (
            <>
              <LockedPreview feature="buyout.advanced">
                <BuyoutCalculator
                  startDate={contract.startDate ?? ""}
                  endDate={contract.endDate ?? ""}
                  annualSalary={contract.annualSalary ?? 0}
                  buyoutClause={contract.buyoutClause ?? null}
                  buyoutSchedule={contract.buyoutSchedule ?? []}
                  terminationRules={contract.terminationRules ?? DEFAULT_TERMINATION_RULES}
                  mitigationRequired={!!contract.mitigationRequired}
                />
              </LockedPreview>
              {isAdmin && !demoIpLocked && (
                <LockedPreview feature="buyout.advanced">
                  <BuyoutTermsEditor
                    contractId={contract.id}
                    initialSchedule={contract.buyoutSchedule ?? []}
                    initialRules={contract.terminationRules ?? DEFAULT_TERMINATION_RULES}
                    initialMitigationRequired={!!contract.mitigationRequired}
                  />
                </LockedPreview>
              )}
            </>
          )}
        </TabsContent>

        <TabsContent value="custom_fields" className="mt-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2"><FileText className="h-5 w-5 text-muted-foreground" /> Custom Fields</CardTitle>
              {isAdmin && (
                <Button size="sm" disabled={cfSaving} onClick={async () => {
                  setCfSaving(true);
                  try {
                    const token = await getToken();
                    if (!token) return;
                    const result = await saveCustomFieldValues("contract", contractId, cfValues, token);
                    if (result.ok) {
                      toast({ title: "Custom fields saved" });
                    } else {
                      toast({ title: "Failed to save custom fields", variant: "destructive" });
                    }
                  } finally {
                    setCfSaving(false);
                  }
                }}>
                  {cfSaving ? "Saving…" : "Save Changes"}
                </Button>
              )}
            </CardHeader>
            <CardContent>
              <CustomFieldsSection
                entityType="contract"
                entityId={contractId}
                readOnly={!isAdmin}
                values={isAdmin ? cfValues : undefined}
                onChange={isAdmin ? (key, value) => setCfValues((prev) => ({ ...prev, [key]: value })) : undefined}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="documents" className="mt-6 space-y-6">
          <ContractDocuments contractId={contract.id} />
          {isAdmin ? <ContractGuestsPanel contractId={contract.id} /> : null}
        </TabsContent>

        <TabsContent value="comments" className="mt-6">
          <CommentsSection entityType="contract" entityId={contractId} />
        </TabsContent>

        <TabsContent value="history" className="mt-6">
          <ContractHistory contractId={contract.id} />
        </TabsContent>

        <TabsContent value="compare" className="mt-6">
          <ContractCompare contractId={contract.id} isAdmin={isAdmin} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
